import React, { useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { shallowEqual, useSelector } from 'react-redux'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import { Button, Card, CardContent, Grid, Icon } from '@mui/material'
import { INDICATORS_COLORS } from 'utils/constants/ColorTheme'
import MultiAutocomplete from 'components/forms/MultiAutocomplete'
import SimpleCheckbox from 'components/forms/SimpleCheckbox'
import { getTypeModule } from 'utils/StationUtils'
import Input from 'components/forms/Input'
import { hasValue } from 'utils/NumberUtil'
import FilterInsertModal from 'components/modal/FilterInsertModal'
import { DEFAULT_CONTRIBUTOR_TYPE, FILTER_TYPE } from 'administration/components/user/constants/UserConstants'
import { compact, uniq } from 'lodash'
import Select from 'components/forms/Select'
import useSandreList from 'utils/customHook/useSandreList'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { CRISIS, NO_DATA, NO_INDICATOR, NORMAL, VIGILANCE } from 'productionUnit/utils/useProductionUnitsFilter'

// L'ordre de cette liste est celui d'affichage des pastilles de statut.
const INDICATOR_CHECKBOXES = [
    { value: CRISIS, i18nKey: 'crisis', backgroundColor: INDICATORS_COLORS.RED, fontColor: 'white' },
    { value: VIGILANCE, i18nKey: 'vigilance', backgroundColor: INDICATORS_COLORS.YELLOW, fontColor: 'black' },
    { value: NORMAL, i18nKey: 'normale', backgroundColor: INDICATORS_COLORS.BLUE, fontColor: 'white' },
    { value: NO_DATA, i18nKey: 'noData', backgroundColor: INDICATORS_COLORS.GREY, fontColor: 'white' },
    { value: NO_INDICATOR, i18nKey: 'notDetermined', backgroundColor: INDICATORS_COLORS.BLACK, fontColor: 'white' },
]

const ProductionUnitFilterPanel = ({
    stationType,
    data = [],

    onChangeSearchValue = () => { },
    setFilterResults = () => { },
    setFilter = () => { },
    setCity = () => { },
    setSector = () => { },
    setAdmin = () => { },
    setOperator = () => { },

    defaultSearchValue,
    defaultFilter,
    defaultFilterResults,
    defaultCity,
    defaultSector,
    defaultAdmin,
    defaultOperator,

    defaultIndicators = [NO_INDICATOR, NORMAL, VIGILANCE, CRISIS, NO_DATA],
    setFilterIndicators = () => { },

    children,
    onSearch = () => { },
    onReset, // optionnel : affiche le bouton Réinitialiser
}) => {
    const {
        contributors,
        cities,
        sectors,
        productionUnits,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        cities: store.CityReducer.cities,
        sectors: store.SectorReducer.sectors,
        productionUnits: store.ProductionUnitReducer.productionUnits,
    }), shallowEqual)

    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState(defaultSearchValue)
    const [tmpFilter, setTmpFilter] = useState(defaultFilter || FILTER_TYPE.NOFILTER)
    const [tmpFilterResults, setTmpFilterResults] = useState(defaultFilterResults || [])
    const [tmpCity, setTmpCity] = useState(defaultCity)
    const [tmpSector, setTmpSector] = useState(defaultSector)
    const [tmpIndicators, setTmpIndicators] = useState(defaultIndicators)
    const [tmpAdmin, setTmpAdmin] = useState(defaultAdmin || [])
    const [tmpOperator, setTmpOperator] = useState(defaultOperator || [])
    const [reset, setReset] = useState(false)

    const typeModule = getTypeModule(stationType)
    const contributorsList = useSandreList(SANDRE.CONTRIBUTOR_TYPES)
    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const onValidate = (newSearch) => {
        setReset(tmpFilter !== FILTER_TYPE.NOFILTER)
        onChangeSearchValue(newSearch || setSearch)
        setFilterResults(tmpFilterResults)
        setFilter(tmpFilter)
        setCity(tmpCity)
        setSector(tmpSector)
        setFilterIndicators(tmpIndicators)
        setAdmin(tmpAdmin)
        setOperator(tmpOperator)
        onSearch()
    }

    const onResetButton = () => {
        setSearch()
        setTmpFilterResults([])
        setTmpFilter(FILTER_TYPE.NOFILTER)
        setTmpCity([])
        setTmpSector([])
        setTmpIndicators([NO_INDICATOR, NORMAL, VIGILANCE, CRISIS, NO_DATA])
        setTmpAdmin([])
        setTmpOperator([])
        onReset()
    }

    const boCodes = uniq(data.map(d => d[`${contributorsList.find(c => c.code === referentType)?.name}Code`]))
    const opCodes = uniq(data.map(d => d[`${contributorsList.find(c => c.code === operatorType)?.name}Code`]))
    const filteredTowns = compact(productionUnits?.map(u => u.townCode))

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1}>
                    <Grid size={4}>
                        <Input
                            tooltip={i18n.searchThenEnter}
                            title={i18n.search}
                            value={search}
                            onChange={setSearch}
                            onEnterKeyPress={(value) => {
                                setSearch(value)
                                onValidate(value)
                            }}
                            clearFunction
                            data-cy='search'
                        />
                    </Grid>
                    <Grid size={3.5}>
                        <SimpleFilterSelect
                            stationType={stationType}
                            onChange={(filterStations, newFilter) => {
                                setReset(false)
                                setTmpFilterResults(filterStations)
                                setTmpFilter(newFilter)
                            }}
                            nullLabel={i18n.allStations}
                            stations={data}
                            value={reset ? FILTER_TYPE.NOFILTER : tmpFilter}
                        />
                    </Grid>
                    <Grid container size={0.5} alignItems='center'>
                        <Icon sx={{ cursor: 'pointer' }} onClick={() => setOpen(true)} data-cy='open-filter' fontSize='md'>
                            {hasValue(tmpFilter) && tmpFilter !== FILTER_TYPE.BOOKMARK && tmpFilter !== FILTER_TYPE.NOFILTER ? 'edit' : 'note_add'}
                        </Icon>
                    </Grid>
                    <Grid size={4}>
                        <MultiAutocomplete
                            elements={sectors}
                            label={i18n.sectors}
                            onChange={setTmpSector}
                            values={tmpSector}
                            clearFunction
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={cities.filter(c => filteredTowns.includes(c.code))}
                            label={i18n.city}
                            onChange={setTmpCity}
                            value={tmpCity}
                            clearFunction
                            noNullValue
                        />
                    </Grid>
                    <Grid size={4}>
                        <MultiAutocomplete
                            elements={contributors.filter(d => boCodes.includes(d.id))}
                            label={contributorsList.find(c => c.code === referentType)?.name}
                            onChange={setTmpAdmin}
                            values={tmpAdmin}
                            clearFunction
                        />
                    </Grid>
                    <Grid size={4}>
                        <MultiAutocomplete
                            elements={contributors.filter(d => opCodes.includes(d.id))}
                            label={contributorsList.find(c => c.code === operatorType)?.name}
                            onChange={setTmpOperator}
                            values={tmpOperator}
                            clearFunction
                        />
                    </Grid>
                    {children && (
                        <Grid size={12}>
                            {children}
                        </Grid>
                    )}
                    <Grid container size={12} justifyContent='space-around'>
                        {INDICATOR_CHECKBOXES.map(({ value, i18nKey, backgroundColor, fontColor }) => (
                            <Grid size={2.4} key={value} spacing={1} >
                                <SimpleCheckbox
                                    label={i18n[i18nKey]}
                                    checked={tmpIndicators.includes(value)}
                                    onToggle={() => setTmpIndicators(prev => prev.includes(value) ? prev.filter(p => p !== value) : [value, ...prev])}
                                    labelStyle={{ padding: '0.25rem 1rem', borderRadius: '5px', backgroundColor, color: `${fontColor} !important` }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <Grid container size={12}>
                        <Grid offset={'auto'}>
                            {onReset && (
                                <Button
                                    variant='outlined'
                                    fullWidth
                                    onClick={onResetButton}
                                >
                                    {i18n.reinit}
                                </Button>
                            )}
                        </Grid>
                        <Button
                            variant='contained'
                            onClick={() => onValidate()}
                            data-cy='searchBtn'
                        >
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
            <FilterInsertModal
                setOpen={setOpen}
                open={open}
                selectedFilter={tmpFilter}
                module={typeModule}
                isNew={!hasValue(tmpFilter) || tmpFilter === FILTER_TYPE.BOOKMARK || tmpFilter === FILTER_TYPE.NOFILTER}
                onChange={setFilter}
            />
        </Card>
    )
}

ProductionUnitFilterPanel.propTypes = {
    stationType: PropTypes.string,
    onChangeSearchValue: PropTypes.func,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    setFilterResults: PropTypes.func,
    setFilter: PropTypes.func,
    filter: PropTypes.number,
    setSector: PropTypes.func,
    sector: PropTypes.number,
    setCity: PropTypes.func,
    city: PropTypes.number,
    defaultSearchValue: PropTypes.string,
    defaultFilter: PropTypes.number,
    defaultFilterResults: PropTypes.arrayOf(PropTypes.number),
    defaultSector: PropTypes.number,
    defaultCity: PropTypes.number,
    defaultIndicators: PropTypes.arrayOf(PropTypes.number),
    setFilterIndicators: PropTypes.func,
    setAdmin: PropTypes.func,
    setOperator: PropTypes.func,
    defaultAdmin: PropTypes.arrayOf(PropTypes.number),
    defaultOperator: PropTypes.arrayOf(PropTypes.number),
    children: PropTypes.node,
    onSearch: PropTypes.func,
    onReset: PropTypes.func,
}


export default ProductionUnitFilterPanel